import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Container, Button, Form, Card, FormControl } from 'react-bootstrap';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Organization, OrganizationSetting, updateOrganization } from '../../services/organization/OrganizationService';
import { setOrganization } from '../../../src/slices/organization';
import useOrganizationsList from '../../hooks/api/organization/useOrganizationsList';
import Avatar from '../../components/layout/avatar/Avatar';
import TextSetting from '../registrations/admin/components/properties/settings/TextSetting';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { useHistory } from 'react-router';
import { FallbackPage } from '../../components/layout/navbar/Navbar';
import OptionList from '../../components/library/inputs/optionlist/OptionList';
import useOrganizationSettingsList from '../../hooks/api/organization/useOrganizationSettingsList';
import ModalComponent from '../../components/modal/ModalComponent';
import useCreateOrganizationSetting from '../../hooks/api/organization/useCreateOrganizationSetting';
import Setting from './settings/Setting';
import PaymentFee from './settings/PaymentFee';
import DefaultSetting from './settings/DefaultSetting';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';

interface Type {
  id: string;
  singleton: boolean;
}

const settingTypes: { [key: string]: Type } = {
  PAYMENTFEE: { id: 'PAYMENTFEE', singleton: true },
  PAYMENTACCOUNT: { id: 'PAYMENTACCOUNT', singleton: false },
};

const Organizations: FC = () => {
  const [organizationName, setName] = useState<string | undefined>('');
  const [currencyOrganization, setCurrency] = useState<string>('usd');
  const [avatarOrganization, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [organizationSecurity, setOrganizationSecurity] = useState<SecurityModel | null>(null);
  const [showAddSetting, setShowAddSetting] = useState<boolean>(false);
  const [availableTypes, setAvailableTypes] = useState<Type[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const { getOrganizations } = useOrganizationsList();
  const avatar = useSelector((state: RootState) => state.organization.organization?.avatar);
  const id = useSelector((state: RootState) => state.organization.organization?.id);
  const name: string | undefined = useSelector((state: RootState) => state.organization.organization?.name);
  const currency: string | undefined = useSelector((state: RootState) => state.organization.organization?.currency);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const inputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state: RootState) => state.user?.current);
  const [organizationSettings, setOrganizationSettings] = useState<any[]>([]);
  const { getOrganizationSettings, isLoading: isSettingsLoading } = useOrganizationSettingsList();
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id);
  const [newSettingContent, setNewSettingContent] = useState<string>('');
  const { createOrganizationSetting } = useCreateOrganizationSetting();

  useEffect(() => {
    setName(name);
    setAvatarUrl(avatar);

    if (currency) {
      setCurrency(currency);
    }
  }, [name, currency]);

  useEffect(() => {
    if (security) {
      setOrganizationSecurity(getSecuritySection(security, SecuritySections.ORGANIZATION));
    }
    if (organizationSecurity && !organizationSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, organizationSecurity]);

  useEffect(() => {
    if (user != null && organizationId != null && user?.isKreezeeAdmin) {
      getOrganizationSettings(organizationId).then((data) => {
        setOrganizationSettings(data || []);
      });
    }
  }, [user, organizationId]);

  const handleAvatarChange = async (e) => {
    setAvatar(e.target.files[0]);
    const newAvatar = URL.createObjectURL(e.target.files[0]);
    setAvatarUrl(newAvatar);
    setButtonDisabled(false);
  };

  const save = async () => {
    const formData: FormData = new FormData();
    formData.append('Organization', JSON.stringify({ currency: currencyOrganization, name: organizationName, id: id }));
    if (avatarOrganization !== null && avatarOrganization !== undefined) {
      formData.append('Avatar', avatarOrganization as File);
    }
    await updateOrganization(id, formData)
      .then(async (response) => {
        if (response.status == 200) {
          createOrganizationObject(id, organizationName, currencyOrganization, response.data.avatar);
          getOrganizations();
        }
      })
      .catch((e) => {
        console.log(e);
      });
    setButtonDisabled(true);
  };

  const openAddSetting = () => {
    setShowAddSetting(true);
    const types: Type[] = [];
    for (const type in settingTypes) {
      if (!settingTypes[type].singleton || !organizationSettings.some((setting) => setting.type === type)) {
        types.push(settingTypes[type]);
      }
    }
    setAvailableTypes(types);
    setSelectedType(types[0]?.id || '');
    (OrganizationSettingComponent[types[0]?.id] || OrganizationSettingComponent['DEFAULT']).setDefault();
  };

  const handleUpload = () => {
    inputRef.current?.click();
  };

  const createOrganizationObject = (
    id: string | undefined,
    name: string | undefined,
    currency: string,
    avatar: string,
  ) => {
    const updatedOrganization: Organization = {
      name: name,
      id: id,
      avatar: avatar,
      currency: currency,
    };
    dispatch(setOrganization(updatedOrganization));
  };

  function validateName(value: string) {
    let error;
    value.trim();
    if (!value) {
      setButtonDisabled(true);
      setName('');
      error = intl.formatMessage({ id: 'organizationMenu.modal.errorName' });
    } else {
      setButtonDisabled(false);
      setName(value);
    }
    return error;
  }

  const OrganizationSettingComponent = {
    PAYMENTFEE: {
      Component: PaymentFee,
      setDefault: () => setNewSettingContent('{"percent":0,"fixed":0,"minimum":0}'),
    },
    DEFAULT: {
      Component: DefaultSetting,
      setDefault: () => setNewSettingContent(''),
    },
  };

  const refreshSettings = async () => {
    if (id) {
      const organizationSettings = await getOrganizationSettings(id);
      setOrganizationSettings(organizationSettings);
    }
  };

  const Component =
    OrganizationSettingComponent[selectedType]?.Component || OrganizationSettingComponent['DEFAULT'].Component;

  return (
    <React.Fragment>
      <div className="page-content organization-page">
        <Container fluid>
          <PageTitle title={intl.formatMessage({ id: 'organizationPage.organization' })}></PageTitle>
          <div className="page-body d-flex">
            <div className="image-wrapper d-flex align-items-center justify-content-center">
              <Avatar src={avatarUrl} width={84} height={84}></Avatar>
              <Button variant="primary" size="sm" onClick={handleUpload} disabled={!organizationSecurity?.update}>
                <span className="mdi mdi-camera">
                  <input
                    ref={inputRef}
                    onChange={(e) => handleAvatarChange(e)}
                    className="file-input d-none"
                    type="file"
                  />
                </span>
              </Button>
            </div>
            <div className="name-wrapper d-flex align-items-center mx-4">
              <Form>
                <div className="align-items-center">
                  <div className="form-group col-md-12">
                    <TextSetting
                      value={organizationName}
                      label="organizationPage.empty"
                      size={12}
                      onChange={(val) => {
                        validateName(val);
                      }}
                      readOnlyMode={organizationSecurity?.update == false}
                    />
                    {organizationName == '' && (
                      <div className="name-error">
                        *<FormattedMessage id="organizationPage.nameError"></FormattedMessage>
                      </div>
                    )}
                  </div>
                </div>
                <div className="align-items-center d-flex mt-3">
                  <OptionList
                    value={[currencyOrganization]}
                    options={[
                      { text: 'USD', value: 'usd' },
                      { text: 'CAD', value: 'cad' },
                    ]}
                    onChange={(values) => {
                      if (values[0]) {
                        setCurrency(values[0]);
                        if (organizationName) {
                          validateName(organizationName);
                        }
                      }
                    }}
                    multiSelection={false}
                    className="mx-2"
                  ></OptionList>
                </div>
              </Form>
            </div>
            <div className="button-wrapper d-flex align-items-center">
              <Button variant="primary" size="sm" disabled={buttonDisabled} onClick={save}>
                <span className="mdi mdi-content-save"></span>
                {intl.formatMessage({ id: 'organizationPage.save' })}
              </Button>
            </div>
          </div>
        </Container>
        {user?.isKreezeeAdmin && (
          <Container fluid>
            <Card className="mt-4 p-4 admin-details">
              <div className="mb-4">
                <h4>
                  <FormattedMessage id="organizationPage.organizationSettings.id" />
                </h4>

                <div>{id}</div>
              </div>
              <div className="position-relative">
                {isSettingsLoading && <LoadingSpinner></LoadingSpinner>}
                <div className="d-flex align-items-center mb-3  ">
                  <h4>
                    <FormattedMessage id="organizationPage.organizationSettings.settings" />
                  </h4>
                  <Button variant="primary" className="ms-3" onClick={() => openAddSetting()}>
                    <span className="mdi mdi-plus me-1"></span>
                    <FormattedMessage id="add"></FormattedMessage>
                  </Button>
                </div>
                <div className="settings-list">
                  {organizationSettings.map((setting) => {
                    return <Setting key={setting.id} setting={setting} onRefresh={refreshSettings}></Setting>;
                  })}
                </div>
              </div>
            </Card>
          </Container>
        )}

        <ModalComponent
          modalHeading={intl.formatMessage({ id: 'organizationPage.organizationSettings.addSetting' })}
          modalSize={'lg'}
          show={showAddSetting}
          buttons={[
            {
              text: intl.formatMessage({ id: 'cancel' }),
              buttonVariant: 'secondary',
              onClick: () => setShowAddSetting(false),
            },
            {
              text: intl.formatMessage({ id: 'add' }),
              buttonVariant: 'primary',
              onClick: async () => {
                if (id && user) {
                  const setting: OrganizationSetting = {
                    organizationId: id,
                    type: selectedType,
                    content: newSettingContent,
                    creationDate: new Date().toISOString(),
                    modificationDate: new Date().toISOString(),
                  };

                  await createOrganizationSetting(id, setting);
                  const settings = await getOrganizationSettings(id);
                  setOrganizationSettings(settings || []);
                }
                setShowAddSetting(false);
              },
            },
          ]}
          closeSettings={() => setShowAddSetting(false)}
        >
          <div className="w-100 px-5">
            <FormControl
              name={id}
              as="select"
              value={selectedType}
              onChange={(e) => {
                const type = OrganizationSettingComponent[e.target.value] || OrganizationSettingComponent['DEFAULT'];
                type.setDefault();
                setSelectedType(e.target.value);
              }}
              className="form-control mb-3"
            >
              {availableTypes.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id}
                </option>
              ))}
            </FormControl>
            <Component
              content={newSettingContent}
              onChange={(content) => setNewSettingContent(content)}
              editMode={true}
            ></Component>
          </div>
        </ModalComponent>
      </div>
    </React.Fragment>
  );
};

export default Organizations;
